import { getAcsDatasetYear } from "./acsReleaseConfig";

const inFlight = new Map();

function censusKeyQuery() {
  const k =
    typeof process !== "undefined" && process.env.REACT_APP_CENSUS_API_KEY
      ? String(process.env.REACT_APP_CENSUS_API_KEY).trim()
      : "";
  return k ? `&key=${encodeURIComponent(k)}` : "";
}

function cleanCount(val) {
  const n = Number(val);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

function cleanMoney(val) {
  const n = Number(val);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

function cleanPct(val) {
  const n = Number(val);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return n;
}

function pickRow(json) {
  if (!Array.isArray(json) || json.length < 2) return null;
  const [header, ...rows] = json;
  if (!rows.length) return null;
  const row = rows[0];
  const o = {};
  header.forEach((h, i) => {
    o[h] = row[i];
  });
  return o;
}

/**
 * @param {string} zcta5
 * @returns {Promise<{
 *   zcta: string,
 *   displayName: string,
 *   acsYear: number,
 *   totalPopulation: number|null,
 *   pctAge65Plus: number|null,
 *   medianHouseholdIncome: number|null,
 *   pctPrivateInsurance: number|null,
 *   pctPublicCoverage: number|null,
 *   pctUninsured: number|null,
 *   pctInsuredApprox: number|null,
 *   pctMedicaidMeansTested: number|null,
 *   pctMale: number|null,
 *   pctFemale: number|null,
 * } | null>}
 */
export async function fetchZctaAcsSnapshot(zcta5) {
  const z = String(zcta5 || "").replace(/\D/g, "").slice(0, 5).padStart(5, "0");
  if (!z || z === "00000") return null;

  const year = getAcsDatasetYear();
  const cacheKey = `${year}:${z}`;
  if (inFlight.has(cacheKey)) return inFlight.get(cacheKey);

  const keyQ = censusKeyQuery();
  const geo = `for=zip%20code%20tabulation%20area:${encodeURIComponent(z)}`;

  const profileUrl =
    `https://api.census.gov/data/${year}/acs/acs5/profile?` +
    `get=NAME,DP05_0001E,DP05_0002PE,DP05_0003PE,DP05_0024PE,DP03_0062E,DP03_0097PE,DP03_0098PE,DP03_0099PE&${geo}${keyQ}`;

  const subjectUrl =
    `https://api.census.gov/data/${year}/acs/acs5/subject?` +
    `get=NAME,S2704_C03_006E&${geo}${keyQ}`;

  const p = Promise.all([fetch(profileUrl), fetch(subjectUrl)])
    .then(async ([rp, rs]) => {
      if (!rp.ok) throw new Error(`Census profile request failed (${rp.status})`);
      if (!rs.ok) throw new Error(`Census subject request failed (${rs.status})`);
      const [jp, js] = await Promise.all([rp.json(), rs.json()]);
      const pr = pickRow(jp);
      const sr = pickRow(js);
      if (!pr) return null;

      const pctUninsured = cleanPct(pr.DP03_0099PE);
      const pctInsuredApprox =
        pctUninsured != null ? Math.max(0, Math.min(100, 100 - pctUninsured)) : null;

      return {
        zcta: z,
        displayName: typeof pr.NAME === "string" ? pr.NAME : `ZCTA ${z}`,
        acsYear: year,
        totalPopulation: cleanCount(pr.DP05_0001E),
        pctAge65Plus: cleanPct(pr.DP05_0024PE),
        medianHouseholdIncome: cleanMoney(pr.DP03_0062E),
        pctPrivateInsurance: cleanPct(pr.DP03_0097PE),
        pctPublicCoverage: cleanPct(pr.DP03_0098PE),
        pctUninsured,
        pctInsuredApprox,
        pctMedicaidMeansTested: sr ? cleanPct(sr.S2704_C03_006E) : null,
        pctMale: cleanPct(pr.DP05_0002PE),
        pctFemale: cleanPct(pr.DP05_0003PE),
      };
    })
    .finally(() => {
      inFlight.delete(cacheKey);
    });

  inFlight.set(cacheKey, p);
  return p;
}
